import { ChangeDetectionStrategy, Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { PermissionListBloc } from '@connect-services/client/blocs/permission-list.bloc';
import { RoleListBloc } from '@connect-services/client/blocs/role-list.bloc';
import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { Permission, RolesService } from '@connect-services/client/sdk/api';
import { PermissionsFormService } from '@connect-services/client/services/permissions-form.service';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { InputNumberComponent } from '@connect-services/src/lib/components/input/input-number/input-number.component';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { FormGroupType } from '@shared/client/models/form.model';
import { defaultPaginationParameters, paginationMaxTake } from '@shared/client/models/pagination.model';
import { FormService } from '@shared/client/services/form.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

interface RoleCreateFormType extends FormGroupType {
    maxAssignment: FormControl<number | null>;
    name: FormControl<string>;
    permissions: FormControl<Permission[]>;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CheckboxComponent,
        ComplexModalTemplate,
        InputNumberComponent,
        InputTextComponent,
        RessourcesModule,
        SelectComponent,
    ],
    selector: 'app-role-create-modal',
    templateUrl: './role-create-modal.component.html',
})
export class RoleCreateModalComponent implements OnInit {
    public readonly formGroup: WritableSignal<FormGroup<RoleCreateFormType> | null> =
        signal<FormGroup<RoleCreateFormType> | null>(null);
    public readonly hasAllPermissions: WritableSignal<boolean> = signal(false);
    public readonly optionsAccess: WritableSignal<SelectOption<Permission>[]> = signal([]);
    public readonly optionsManage: WritableSignal<SelectOption<Permission>[]> = signal([]);
    public readonly permissionsFormService = inject(PermissionsFormService);
    public readonly selectedOptionAccess: WritableSignal<SelectOption<Permission>[]> = signal([]);
    public readonly selectedOptionManage: WritableSignal<SelectOption<Permission>[]> = signal([]);

    protected readonly CheckboxState = CheckboxState;
    protected dialogRef = inject(MatDialogRef<RoleCreateModalComponent>);
    protected readonly FormService = FormService;
    protected readonly rolesService = inject(RolesService);

    private readonly permissionListBloc = inject(PermissionListBloc);
    private readonly roleListBloc = inject(RoleListBloc);
    private readonly snackbarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);

    public changeSelection(): void {
        this.hasAllPermissions.set(!this.hasAllPermissions());

        if (this.hasAllPermissions()) {
            this.selectedOptionManage.set([]);
            this.selectedOptionAccess.set([]);
        }
    }

    public closeModal(event: ModalResultEnum): Promise<void> | void {
        return event === ModalResultEnum.CONFIRM ? this.submitForm() : this.dialogRef.close();
    }

    public async ngOnInit(): Promise<void> {
        this.formGroup.set(this.initForm());

        const pageParameters = { ...defaultPaginationParameters };
        await this.permissionListBloc.load({ ...pageParameters, take: paginationMaxTake });

        const sortedPermissions = this.permissionListBloc.value()?.items.sort((permissionA, permissionB) => {
            const permissionAliasA = this.permissionsFormService.convertAlias(permissionA.alias);
            const permissionAliasB = this.permissionsFormService.convertAlias(permissionB.alias);

            return this.translocoService
                .translate(permissionAliasA)
                .localeCompare(this.translocoService.translate(permissionAliasB));
        });

        this.splitOptions(sortedPermissions ?? []);
    }

    public onSelectOptionChange(): void {
        this.formGroup()?.controls.permissions.setValue([
            ...this.selectedOptionManage().map((option) => option.value),
            ...this.selectedOptionAccess().map((option) => option.value),
        ]);
    }

    public async submitForm(): Promise<void> {
        if (this.formGroup()?.invalid) {
            return;
        }

        await this.createRole();
        this.snackbarService.openSuccessSnackBar('roleCreatedSuccess');
        await this.roleListBloc.load({}, { refresh: true, useLastLoadParameters: true });
        this.dialogRef.close();
    }

    private async createRole(): Promise<void> {
        const name: string = this.formGroup()!.controls.name.value!;
        const maxNumberOfAssignments = this.formGroup()?.controls.maxAssignment?.value ?? undefined;

        const createdRole = await firstValueFrom(
            this.rolesService.roleControllerPost({
                createRoleDto: {
                    maxNumberOfAssignments: maxNumberOfAssignments,
                    name: name,
                },
            }),
        );

        const permissionIds: string[] = this.hasAllPermissions()
            ? this.getAllPermissions().map((permission) => permission.id)
            : (this.formGroup()?.controls.permissions.value.map((permission: Permission) => permission.id) ?? []);

        await firstValueFrom(
            this.rolesService.roleControllerPatch({
                id: createdRole.id,
                updateRoleDto: {
                    permissions: permissionIds,
                },
            }),
        );
    }

    private getAllPermissions(): Permission[] {
        return [...this.optionsManage(), ...this.optionsAccess()].map((option) => option.value);
    }

    private initForm(): FormGroup<RoleCreateFormType> {
        return new FormGroup<RoleCreateFormType>({
            maxAssignment: new FormControl<number | null>(null, {
                nonNullable: false,
            }),

            name: new FormControl<string>('', {
                nonNullable: true,
                validators: [Validators.maxLength(FormService.VARCHAR_SMALL_LENGTH), Validators.required],
            }),
            permissions: new FormControl<Permission[]>([], {
                nonNullable: true,
            }),
        });
    }

    private splitOptions(permissionList: Permission[]): void {
        const access: SelectOption<Permission>[] = [];
        const manage: SelectOption<Permission>[] = [];

        for (const permission of permissionList) {
            if (permission.alias.includes('read') && permission.alias !== 'read_GoogleStreetView') {
                access.push({
                    label: this.translocoService.translate(this.permissionsFormService.convertAlias(permission.alias)),
                    value: permission,
                });
            }
            if (permission.alias.includes('manage')) {
                manage.push({
                    label: this.translocoService.translate(this.permissionsFormService.convertAlias(permission.alias)),
                    value: permission,
                });
            }
        }

        this.optionsAccess.set(access);
        this.optionsManage.set(manage);
    }
}
